import React, {useState, useEffect, createRef, useCallback} from 'react';
import {
    Box,
    FormControl,
    FormLabel,
    FormHelperText,
    Checkbox,
    TextField,
    Toolbar,
    Typography,
    Tooltip,
    Button,
    IconButton,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
} from '@material-ui/core';
import get from 'lodash.get';
import {lighten, makeStyles} from '@material-ui/core/styles';
import clsx from 'clsx';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import {Controller, useFormContext} from 'react-hook-form';
import {sumToFixed} from 'utils/arrays';

const useFieldStyles = makeStyles({
    root: {
        transformOrigin: 'top right',
    },
    formControl: {
        left: 'auto',
        right: 0,
    },
});
const useStyles = makeStyles(theme => ({
    highlight:
        theme.palette.type === 'light'
            ? {
                  color: theme.palette.secondary.main,
                  backgroundColor: lighten(theme.palette.secondary.light, 0.85),
              }
            : {
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.secondary.dark,
              },
    grow: {
        flexGrow: 1,
    },
    title: {
        paddingLeft: '1em',
        flex: '1 1 100%',
        color: theme.palette.text.secondary,
    },
    numberInput: {
        textAlign: 'right',
    },
    total: {
        fontWeight: 'bold',
        color: theme.palette.text.secondary,
    },
}));

const AutoAddition = ({
    dataHeading,
    description,
    name,
    error,
    value,
    onChange,
    setMYD,
    totalText = '## Total',
}) => {
    const fieldClasses = useFieldStyles();
    const classes = useStyles();

    // UI state
    const [data, setData] = useState(value || []);
    const [selected, setSelected] = useState([]);
    const [customError, setCustomError] = useState(null);
    const totalNum = data.length;
    const numSelected = selected.length;

    // Input state
    const [input, setInput] = useState('');
    const [touched, setTouched] = useState(false);
    const inputRow = createRef();
    const inputField = createRef();
    const container = createRef();

    const isSelected = entry => selected.indexOf(entry) !== -1;

    const handleRowClick = (event, index) => {
        const indexInSelection = selected.indexOf(index);
        let newSelected = [];

        if (indexInSelection === -1) {
            newSelected = selected.concat(index);
        } else if (indexInSelection === 0) {
            newSelected = selected.slice(1);
        } else if (indexInSelection === selected.length - 1) {
            newSelected = selected.slice(0, -1);
        } else {
            newSelected = newSelected.concat(
                selected.slice(0, indexInSelection),
                selected.slice(indexInSelection + 1)
            );
        }

        setSelected(newSelected);
    };

    const handleSelectAllClick = event => {
        if (event.target.checked) {
            setSelected(data.map((d, i) => i));
        } else {
            setSelected([]);
        }
    };

    const deleteSelected = () => {
        setCustomError(null);

        const newData = data.filter((entry, i) => {
            return selected.indexOf(i) === -1;
        });

        setData(newData);
        onChange(newData);
        setSelected([]);
    };

    const handleAdd = useCallback(() => {
        if (input) {
            try {
                setCustomError(null);
                const float = Number.parseFloat(input);
                if (Number.isNaN(float)) {
                    throw new Error('invalid input');
                }
                // all checks passed. save state and update dependent component
                const newData = [...data, float];
                setData(newData);
                onChange(newData);
            } catch (e) {
                console.warn(
                    '[AutoAdditionField] received unexpected input',
                    e
                );
                // no-op
            } finally {
                setInput('');
            }
        }
    }, [data, input, onChange]);

    useEffect(() => {
        const inputRef = inputRow.current;
        const handleInput = e => {
            // Enter
            if (e.keyCode === 13) {
                handleAdd();
            }
        };
        inputRef.addEventListener('keydown', handleInput);
        inputRef.addEventListener('blur', handleInput);

        return () => {
            inputRef.removeEventListener('keydown', handleInput);
            inputRef.removeEventListener('blur', handleInput);
        };
    }, [handleAdd, inputRow]);

    useEffect(() => {
        // Focus year input when data changes
        if (inputField.current && touched) {
            inputField.current.querySelector('input').focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalNum]);

    const firstClick = useCallback(e => {
        if (!totalNum && !touched) {
            inputField.current?.querySelector('input').focus();
        }
    }, [totalNum, touched, inputField]);

    useEffect(() => {
        const containerRef = container.current;
        containerRef?.addEventListener('click', firstClick)
        return () => {
            containerRef?.removeEventListener('click', firstClick);
        }
    }, [container, firstClick]);

    return (
        <>
            <Paper pl={1} ref={container}>
                <Toolbar
                    disableGutters
                    className={clsx(classes.root, {
                        [classes.highlight]: numSelected > 0,
                    })}
                >
                    {!numSelected && (
                        <Typography
                            variant='h6'
                            component='div'
                            className={classes.title}
                        >
                            {description}
                        </Typography>
                    )}
                    {numSelected > 0 && (
                        <>
                            <Typography className={classes.title}>
                                {numSelected} selected
                            </Typography>
                            <Tooltip title='Delete'>
                                <IconButton
                                    aria-label='delete'
                                    data-cy='field-autoAddition-delete'
                                    onClick={deleteSelected}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </Toolbar>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding='checkbox'>
                                    <Checkbox
                                        disabled={!totalNum}
                                        indeterminate={
                                            !totalNum || (
                                                numSelected > 0 &&
                                                numSelected < totalNum
                                            )
                                        }
                                        checked={
                                            totalNum > 0 &&
                                            numSelected === totalNum
                                        }
                                        onChange={handleSelectAllClick}
                                        inputProps={{
                                            'aria-label': 'select all entries',
                                        }}
                                    />
                                </TableCell>
                                <TableCell align='right'>
                                    {dataHeading}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map((entry, i) => {
                                const rowIsSelected = isSelected(i);
                                return (
                                    <TableRow
                                        hover
                                        role='checkbox'
                                        onClick={event =>
                                            handleRowClick(event, i)
                                        }
                                        aria-checked={rowIsSelected}
                                        tabIndex={-1}
                                        key={entry}
                                        selected={rowIsSelected}
                                    >
                                        <TableCell padding='checkbox'>
                                            <Checkbox
                                                checked={rowIsSelected}
                                                aria-label={entry}
                                                data-cy={`field-autoAddition-row-${i}`}
                                            />
                                        </TableCell>
                                        <TableCell align='right'>
                                            {entry}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            <TableRow ref={inputRow}>
                                <TableCell>
                                    <Tooltip title='Add'>
                                        <Button
                                            variant='contained'
                                            onClick={handleAdd}
                                            size='small'
                                            color='secondary'
                                            data-cy={`form-field-${name}-add`}
                                            disabled={!input}
                                            startIcon={<AddIcon />}
                                        >
                                            Add
                                        </Button>
                                    </Tooltip>
                                </TableCell>
                                <TableCell align='right'>
                                    <TextField
                                        ref={inputField}
                                        inputProps={{
                                            className: classes.numberInput,
                                            'data-cy': `form-field-${name}-input`,
                                        }}
                                        value={input}
                                        type='number'
                                        onChange={e => setInput(e.target.value)}
                                        InputLabelProps={{
                                            classes: fieldClasses,
                                        }}
                                        label={dataHeading}
                                        onFocus={() => setTouched(true)}
                                    />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box p={2} align='right'>
                    <Typography
                        className={classes.total}
                        variant='subtitle1'
                        align='right'
                    >
                        {totalText.replace('##', sumToFixed(data, 2))}
                    </Typography>
                </Box>
            </Paper>
            <FormHelperText error>{customError}</FormHelperText>
        </>
    );
};

const AutoAdditionField = ({
    label,
    description,
    name,
    required,
    dataHeading,
    totalText,
    style,
}) => {
    const {control, errors} = useFormContext();
    const error = get(errors, name);

    return (
        <FormControl error={!!error} margin='normal' style={{minWidth: '50%'}}>
            <Box mb={1}>
                <FormLabel id={`${name}-label`} required={required}>
                    {label}
                </FormLabel>
            </Box>
            <Controller
                render={p => (
                    <AutoAddition
                        name={p.name}
                        error={!!error}
                        onBlur={p.onBlur}
                        onChange={p.onChange}
                        value={p.value}
                        description={description}
                        dataHeading={dataHeading}
                        totalText={totalText}
                    />
                )}
                name={name}
                control={control}
            />
            <FormHelperText>{error?.message}</FormHelperText>
        </FormControl>
    );
};

export default AutoAdditionField;
